import BottomSheet, {
	BottomSheetFooter,
	BottomSheetTextInput,
	BottomSheetView,
} from "@gorhom/bottom-sheet";
import type { BottomSheetDefaultFooterProps } from "@gorhom/bottom-sheet/lib/typescript/components/bottomSheetFooter/types";
import type { WordLimit } from "@memorang/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Keyboard } from "react-native";
import { Button, Text } from "react-native-paper";
import { useAppTheme } from "../useAppTheme";
import Box from "./Box";
import SheetHandle from "./SheetHandle";
import { WordCountChip } from "./StudySession/SessionContent/WritingToolbar/WordCountChip";

type Props = {
	value?: string;
	onChangeText: (text: string) => void;
	placeholder: string;
	wordCount: number;
	wordCountText?: string;
	wordLimit?: WordLimit;
	maxLength?: number;
	disabled?: boolean;
};

const CustomBottomSheetTextInput = ({
	value,
	onChangeText,
	placeholder,
	wordCount,
	wordCountText,
	wordLimit,
	maxLength,
	disabled,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	const [snapIndex, setSnapIndex] = useState(0);
	const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
	const bottomSheetRef = useRef<BottomSheet>(null);

	useEffect(() => {
		const showSubscription = Keyboard.addListener("keyboardDidShow", () => {
			setIsKeyboardOpen(true);
		});
		const hideSubscription = Keyboard.addListener("keyboardDidHide", () => {
			setIsKeyboardOpen(false);
			bottomSheetRef.current?.snapToIndex(0);
		});

		return () => {
			showSubscription.remove();
			hideSubscription.remove();
		};
	}, []);

	const onBottomSheetChange = useCallback((index: number) => {
		setSnapIndex(index);
	}, []);

	const handleDismissBottomSheet = useCallback(() => {
		Keyboard.dismiss();
	}, []);

	const pluralWord = t("common.word", { count: wordCount });

	const renderFooter = useCallback(
		(props: BottomSheetDefaultFooterProps) => {
			return (
				<BottomSheetFooter
					{...props}
					style={{
						paddingTop: 8,
						alignItems: "flex-end",
						paddingBottom: 16,
						paddingRight: 16,
					}}
				>
					<Button
						mode="contained"
						icon="send"
						uppercase
						disabled={!value}
						onPress={handleDismissBottomSheet}
					>
						{t("writing.submit")}
					</Button>
				</BottomSheetFooter>
			);
		},
		[handleDismissBottomSheet, value, t],
	);

	return (
		<BottomSheet
			ref={bottomSheetRef}
			snapPoints={[260, "100%"]}
			handleComponent={() => (
				<Box paddingBottom={8}>
					<SheetHandle />
					<Box
						flexDirection="row"
						justifyContent="space-between"
						alignItems="center"
						paddingHorizontal={16}
						paddingBottom={12}
						gap={8}
						borderBottomWidth={1}
						borderBottomColor={theme.colors.surfaceVariant}
					>
						<Text variant="bodyLarge">{t("writing.title")}</Text>
						<WordCountChip
							wordCount={wordCount}
							wordLimit={wordLimit}
							pluralWord={pluralWord}
							overrideText={wordCountText}
						/>
					</Box>
				</Box>
			)}
			onChange={onBottomSheetChange}
			backgroundStyle={{
				borderStyle: "solid",
				borderWidth: 2,
				borderColor: theme.palette.divider,
				borderTopLeftRadius: 25,
				borderTopRightRadius: 25,
				backgroundColor: theme.colors.elevation.level1,
			}}
			footerComponent={isKeyboardOpen ? renderFooter : undefined}
			keyboardBlurBehavior="restore"
			enableDynamicSizing={false}
			keyboardBehavior={"interactive"}
			android_keyboardInputMode="adjustPan"
		>
			<BottomSheetView
				style={{
					paddingBottom: 0,
					paddingHorizontal: 16,
					gap: 16,
					flex: snapIndex === 2 ? 1 : undefined,
				}}
			>
				<BottomSheetTextInput
					value={value}
					onChangeText={onChangeText}
					multiline
					selectionColor={theme.colors.primary}
					placeholderTextColor={theme.colors.onSurfaceVariant}
					placeholder={placeholder}
					autoComplete="off"
					editable={!disabled}
					autoCorrect={false}
					spellCheck={false}
					autoCapitalize="none"
					maxLength={maxLength}
					style={{
						flex: snapIndex === 2 ? 1 : undefined,
						paddingTop: 16,
						paddingBottom: isKeyboardOpen ? 24 : 16,
						textAlignVertical: "top",
						color: theme.colors.onSurface,
					}}
				/>
			</BottomSheetView>
		</BottomSheet>
	);
};

export default CustomBottomSheetTextInput;
